import React, { useState, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import withModal from '../../../../../components/global/modal/withModal';
import DismissButton from '../../../../../components/global/DismissButton';
import Button from '../../../../../components/global/Button';
import PinchZoomPan from '../../../../../components/global/PinchZoomPan';
import useComponentSize from '../../../../../hooks/useComponentSize';
import CardElementPhotoImage from '../CardElementPhotoImage';
import {
  getCardPhotoElementConfigurationImageUrl,
  getInitialPhotoCropbox,
} from '../helpers';
import { isMobile } from '../../../../../util/device-detector';
import NativeActivityIndicator from '../../../../../components/global/NativeActivityIndicator';
import useVirtualPageView from '../../../../../apps/pro/services/analytics/useVirtualPageView';
import { IMAGE_SIZE_CODE } from '../../../../../constants/image-size-codes';
import WatermarkOverlay from '../../../../../components/global/WatermarkOverlay';
import RotateLeftIcon from '../../../../../components/icons/RotateLeftIcon';

const propTypes = {
  onClose: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onPhotoChange: PropTypes.func,
  element: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.shape({
      asset_image: PropTypes.shape({
        id: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
      photo: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
      cropbox: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        scale: PropTypes.number.isRequired,
        rotation: PropTypes.number.isRequired,
      }),
    }).isRequired,
    side: PropTypes.string.isRequired,
    loading: PropTypes.bool,
  }).isRequired,
  overlayingElement: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
  }),
  cardWidth: PropTypes.number,
  cardHeight: PropTypes.number,
  imageSizeCode: PropTypes.string,
  isEntitled: PropTypes.bool,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
    watermarkMode: PropTypes.string,
    wallpaperWatermarkMode: PropTypes.string,
  }),
};

const CardElementPhotoControl = ({
  onClose,
  onPhotoChange,
  onChange,
  element,
  overlayingElement,
  cardWidth = 7.25,
  cardHeight = 5.25,
  imageSizeCode = IMAGE_SIZE_CODE.LARGE,
  isEntitled,
  watermarkSettings,
}) => {
  const [imageLoading, setImageLoading] = useState(true);
  const placeholderRef = useRef(null);
  const { cardElement, cardElementConfiguration } = element;
  const { photo } = cardElementConfiguration ?? {};
  const [cropbox, setCropbox] = useState(
    cardElementConfiguration.cropbox
      ? { ...cardElementConfiguration.cropbox }
      : getInitialPhotoCropbox(),
  );

  useEffect(() => {
    setCropbox({ ...(cardElementConfiguration.cropbox ?? {}) });
  }, [cardElementConfiguration?.cropbox]);

  useVirtualPageView({
    title: 'Create a card - Adjust your photo',
    path: '/element/photo/pan',
  });

  const initialCropbox = useMemo(
    () => ({
      ...(cardElementConfiguration?.cropbox ?? {}),
    }),
    [cardElementConfiguration?.cropbox],
  );

  const hasCropboxChanges = useMemo(
    () =>
      cropbox.x !== initialCropbox.x ||
      cropbox.y !== initialCropbox.y ||
      cropbox.scale !== initialCropbox.scale ||
      cropbox.rotation !== initialCropbox.rotation,
    [cropbox, initialCropbox],
  );

  const imageUrl = getCardPhotoElementConfigurationImageUrl(
    cardElementConfiguration,
    imageSizeCode,
    !isEntitled,
    watermarkSettings,
  );

  const ratio =
    (cardElement.frame.height * cardHeight) /
    (cardElement.frame.width * cardWidth);
  const { width } = useComponentSize(placeholderRef);
  const height = width * ratio;
  const showOverlay = overlayingElement?.cardElementConfiguration?.asset_image;
  const hasPhotoForeground =
    !!element?.cardElementConfiguration?.photo?.foreground?.original_url;
  const allowUnlimitedMovement = !!showOverlay || hasPhotoForeground;

  return (
    <div className="card-element-photo-control-content">
      <div className="card-element-control-header">
        <DismissButton onClick={onClose} />

        <span className="ml-0 ml-md-1 card-element-control-header-title">
          Confirm or change your crop below!
          <br />
          This is what your print will look like.
        </span>
      </div>

      <p className="text-center text-faded mb-3">
        {isMobile() ? (
          <span>
            Two-finger pinch to pan, zoom, and change the positioning within the
            photo.
          </span>
        ) : (
          <span className="text-sm d-block">
            Zoom by scrolling or pinching with two fingers on your trackpad and
            click and drag to pan your photo to fit the photo slot properly.
          </span>
        )}
      </p>

      <div
        className={classNames({
          'card-element-photo-placeholder': true,
          'is-loading': imageLoading || element.loading,
        })}
        ref={placeholderRef}
        style={{
          height: `${height}px`,
          // If the ratio is larger than 1 it means the photo slot
          // is portrait and it might not fit very well in height, so shrink
          // the width by some factor to make sure it fits properly.
          width: ratio > 1 ? `${100 - ratio * 22}%` : undefined,
        }}
      >
        {imageLoading && <NativeActivityIndicator size="sm" type="white" />}
        {imageUrl && (
          <PinchZoomPan
            minScale={0.5}
            maxScale={10}
            initialLeft={cropbox.x ? cropbox.x * width : 0}
            initialTop={cropbox.y ? cropbox.y * height : 0}
            initialScale={cropbox.scale || 1}
            initialRotation={cropbox.rotation || 0}
            onChange={({ x, y, scale, rotation }) => {
              setCropbox({
                x: x / width,
                y: y / height,
                scale,
                rotation,
              });
            }}
            controlButtons
            unlimitedMovement={allowUnlimitedMovement}
          >
            <img
              key={imageUrl}
              src={imageUrl}
              alt=""
              onLoad={() => setImageLoading(false)}
            />
          </PinchZoomPan>
        )}

        {showOverlay && (
          <CardElementPhotoImage
            className="card-element-photo-placeholder-overlay"
            src={overlayingElement?.cardElementConfiguration.asset_image?.url}
            cropbox={overlayingElement?.cardElementConfiguration?.cropbox}
            watermarkSettings={watermarkSettings}
          />
        )}

        {!isEntitled &&
          !photo?.foreground?.original_url &&
          photo?.wallpaper_watermark_urls?.[0]?.url !== imageUrl &&
          width > 0 &&
          height > 0 && (
            <div className="watermark-container zoom-pan-overlay">
              <WatermarkOverlay
                text={watermarkSettings?.wallpaperCustomText}
                fontSize={watermarkSettings?.wallpaperFontSize}
                wallpaperMode={watermarkSettings?.wallpaperWatermarkMode}
                imageUrl={watermarkSettings?.wallpaperAssetImage?.url}
              />
            </div>
          )}
      </div>

      <Button
        btnStyle="link"
        className="card-element-photo-controls-revert"
        disabled={!hasCropboxChanges}
        onClick={() => {
          setCropbox({ ...initialCropbox });
        }}
      >
        <RotateLeftIcon color="#fff" size={12} /> Revert Changes
      </Button>

      <div className="card-element-photo-controls">
        {onPhotoChange && (
          <Button btnOutline btnStyle="white" onClick={onPhotoChange}>
            Change photo
          </Button>
        )}

        <Button
          onClick={() => {
            onClose();
            onChange(cropbox);
          }}
          disabled={imageLoading || element?.loading}
        >
          {imageLoading || element?.loading ? 'Loading...' : 'Done'}
        </Button>
      </div>

      {imageUrl && (
        <div
          className={classNames({
            'card-element-photo-control-backdrop': true,
            'is-loading': imageLoading || element.loading,
          })}
          style={{
            backgroundImage: `url(${imageUrl})`,
          }}
          key={imageUrl}
        />
      )}
    </div>
  );
};

CardElementPhotoControl.propTypes = propTypes;

export const CARD_ELEMENT_PHOTO_CONTROL_MODAL =
  'CARD_ELEMENT_PHOTO_CONTROL_MODAL';

export default withModal(CardElementPhotoControl, {
  name: CARD_ELEMENT_PHOTO_CONTROL_MODAL,
  dismissible: false,
  size: 'xs',
  type: 'full',
  hideHeader: true,
  className: 'card-element-photo-control-modal',
});
